import Calendar from '@/components/calendar/Calendar';
import Avatar from '@/components/common/Avatar';
import { ButtonCusWithIcon } from '@/components/common/Button';
import AvartarChange from '@/assets//images/change_photo.svg';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import ImageUploadAccount from '@/components/general/ImageUploadAccount';
import { DATE_YMD_2, DEFAULT_IMAGE_LIMIT_SIZE, LICENSE_TYPE_OPTIONS, MAX_LENGTH_NUMBER, NUMBER_FULL_HALF_VALIDATE_REGEX } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { getAvatarFallbackName, isAdmin, isLicenseType, isRoleEndUser, replaceSpaces } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Combobox from '@/components/common/Combobox';
import { convertFullToHalf } from '@/utils/formatNumber';
import PasswordItem from './components/password/PasswordItem';
import CouponForeverItem from './components/coupon/CouponForeverItem';
import EditEmail from './components/edit-email/EditEmail';
import { LICENSE_TYPE_OPTION } from '@/types/enums';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';

export default observer(function MyAccountGas() {
    // hook
    const { t } = useTranslation();

    // store
    const {
        authStore: { profileGas },
        generalStore: {
            myAccount,
            getMyAccount, editMyAccount,
            licenseTypeGas, getLicenseGas
        },
        fileStore: { uploadFile },
        modalStore: { showAlertModal }
    } = useStore();

    // state
    const [avartarImgUrl, setAvartarImgUrl] = useState<string>('');
    const [beforeImgUrl, setBeforeImgUrl] = useState<string>('');
    const [afterImgUrl, setAfterImgUrl] = useState<string>('');

    // form
    const validateSchema = yup.object().shape({
        avartarImageId: yup
            .number(),
        email: yup
            .string()
            .required('form_error_validate.required'),
        phone: yup
            .string()
            .required('form_error_validate.required')
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        furiganaName: yup
            .string(),
        zipCode: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        licenseType: yup
            .string()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.string().required('form_error_validate.required')
            }),
        licenseTypeOption: yup
            .number()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.string().required('form_error_validate.required')
            }),
        licenseBeforeImageId: yup
            .number()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.number().required('form_error_validate.required'),
                otherwise: _ => yup.number().optional()
            }),
        licenseAfterImageId: yup
            .number()
            .when(['roleId', 'licenseTypeOption'], {
                is: (roleId, licenseTypeOption) => roleId && !isAdmin(Number(roleId)) && Number(licenseTypeOption) !== LICENSE_TYPE_OPTION.MY_NUMBER,
                then: _ => yup.number().required('form_error_validate.required'),
                otherwise: _ => yup.number().optional()
            }),
        licenseExporeDate: yup
            .date(),
        fullname: yup
            .string()
            .required('form_error_validate.required'),
        licenseNumber: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        birthOfDate: yup
            .date()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.date().required('form_error_validate.required'),
                otherwise: _ => yup.date().nullable()
            }),
        address: yup
            .string(),
        roleId: yup
            .number()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            avartarImageId: undefined,
            email: '',
            phone: '',
            furiganaName: '',
            zipCode: '',
            licenseType: '',
            licenseTypeOption: LICENSE_TYPE_OPTION.TRADITIONAL,
            licenseBeforeImageId: undefined,
            licenseAfterImageId: undefined,
            licenseExporeDate: undefined,
            fullname: '',
            licenseNumber: '',
            birthOfDate: undefined,
            address: '',
            roleId: undefined
        }
    });

    // watch form
    const watchLicenseTypeOption = form.watch('licenseTypeOption');
    const watchRoleId = form.watch('roleId');

    const useMyNumberLicense = useMemo((): boolean => {
        return Number(watchLicenseTypeOption) === LICENSE_TYPE_OPTION.MY_NUMBER;
    }, [watchLicenseTypeOption]);

    // lifecycle
    useEffect(() => {
        fetchDataLicense();
        fetchDataAccount();
    }, []);

    // function
    const fetchDataLicense = async () => {
        await flowResult(getLicenseGas());
    };

    const fetchDataAccount = async () => {
        const res = await flowResult(getMyAccount());
        if (res) {
            const {
                avartarImage,
                email,
                phone,
                furiganaName,
                zipCode,
                licenseBeforeImage,
                licenseAfterImage,
                licenseType,
                licenseTypeOption,
                licenseExporeDate,
                fullname,
                licenseNumber,
                birthOfDate,
                address,
                role
            } = res.data;

            if (avartarImage?.path) {
                setAvartarImgUrl(avartarImage.path);
            }

            if (licenseBeforeImage?.path) {
                setBeforeImgUrl(licenseBeforeImage?.path);
            }

            if (licenseAfterImage?.path) {
                setAfterImgUrl(licenseAfterImage?.path);
            }

            form.reset({
                avartarImageId: avartarImage?.id || undefined,
                email: email || '',
                phone: phone ? convertFullToHalf(phone) : '',
                furiganaName: furiganaName || '',
                zipCode: zipCode ? convertFullToHalf(zipCode) : '',
                licenseType: (Array.isArray(licenseType) && licenseType?.length > 0) ?
                    licenseType[0] :
                    '',
                licenseTypeOption: isLicenseType(Number(licenseTypeOption)) ? Number(licenseTypeOption) : undefined,
                licenseBeforeImageId: licenseBeforeImage?.id || undefined,
                licenseAfterImageId: licenseAfterImage?.id || undefined,
                licenseExporeDate: licenseExporeDate ? new Date(licenseExporeDate) : undefined,
                fullname: fullname || '',
                licenseNumber: licenseNumber ? convertFullToHalf(licenseNumber) : '',
                birthOfDate: birthOfDate ? new Date(birthOfDate) : undefined,
                address: address || '',
                roleId: role?.id || undefined
            });
        }
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const fileType = file.type;
            if (!fileType.includes('image')) {
                showAlertModal({
                    id: 'error',
                    type: 'error',
                    content: t('messages.only_upload_image')
                });
                e.target.value = '';
                return;
            }

            const maxSizeBytes = DEFAULT_IMAGE_LIMIT_SIZE * 1024 * 1024;
            if (file.size > maxSizeBytes) {
                showAlertModal({
                    id: 'error',
                    type: 'error',
                    content: t('messages.exceed_size_limit', { size: DEFAULT_IMAGE_LIMIT_SIZE })
                });
                e.target.value = '';
                return;
            }

            const res = await flowResult(
                uploadFile({ files: file })
            );

            if (res?.ok) {
                const newImagePreview = URL.createObjectURL(file);
                setAvartarImgUrl(newImagePreview);
                if (res.data?.length) {
                    form.setValue('avartarImageId', res.data[0].id);
                }
            }
        }
        e.target.value = '';
    };

    const onSubmit = async (data: FormData) => {
        const {
            avartarImageId,
            email,
            phone,
            furiganaName,
            zipCode,
            licenseType,
            licenseTypeOption,
            licenseBeforeImageId,
            licenseAfterImageId,
            licenseExporeDate,
            fullname,
            licenseNumber,
            birthOfDate,
            address
        } = data;

        const dataSubmit = {
            avartarImageId,
            email,
            phone: phone ? convertFullToHalf(phone) : '',
            furiganaName,
            zipCode: zipCode ? convertFullToHalf(zipCode) : '',
            licenseType: licenseType && !isNaN(Number(licenseType)) ? [Number(licenseType)] : [],
            licenseTypeOption: licenseTypeOption || null,
            licenseBeforeImageId: licenseBeforeImageId || null,
            licenseAfterImageId: (!useMyNumberLicense && licenseAfterImageId) ? licenseAfterImageId : null,
            minorDriverLicense: null,
            licenseExporeDate: formatDateTime((licenseExporeDate || ''), DATE_YMD_2),
            fullname,
            licenseNumber: licenseNumber ? convertFullToHalf(licenseNumber) : '',
            birthOfDate: formatDateTime((birthOfDate || ''), DATE_YMD_2),
            address
        };

        const res = await flowResult(editMyAccount(dataSubmit));
        if (res) {
            getMyAccount();
            showAlertModal({
                id: 'edit-my-account-success',
                type: 'success',
                content: t('messages.update_success')
            });
        }
    };

    const uploadSuccessBefore = (value) => {
        if (useMyNumberLicense) {
            return;
        }

        form.setValue('licenseBeforeImageId', value?.licenseImage?.id || null);

        form.setValue('fullname', value?.licenseInfo?.name ? replaceSpaces(value?.licenseInfo?.name) : '');
        value?.licenseInfo?.dateOfBirth ?
            form.setValue('birthOfDate', new Date(value?.licenseInfo?.dateOfBirth)) :
            form.reset({
                ...form.getValues(),
                birthOfDate: undefined
            });
        value?.licenseInfo?.dateOfExpiration ?
            form.setValue('licenseExporeDate', new Date(value?.licenseInfo?.dateOfExpiration)) :
            form.reset({
                ...form.getValues(),
                licenseExporeDate: undefined
            });
        form.setValue('licenseNumber', value?.licenseInfo?.licenseNumber ? replaceSpaces(value?.licenseInfo?.licenseNumber) : '');
        form.setValue('address', value?.licenseInfo?.address ? replaceSpaces(value?.licenseInfo?.address) : '');
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='mt-0'>
                <div className='layout-card'>
                    {/* First info */}
                    <div className='mb-6'>
                        <div className='flex gap-6 justify-between mb-6'>
                            <h2 className='title-2 whitespace-nowrap'>{t('words_title.basic_info')}</h2>
                            <PasswordItem />
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                            <div className='col-span-1 md:col-span-1 min-h-40'>
                                <div className='flex flex-col justify-center items-center'>
                                    <FormField
                                        control={form.control}
                                        name='avartarImageId'
                                        render={() => (
                                            <FormItem>
                                                <Label htmlFor='avartar-image' className='relative cursor-pointer'>
                                                    <Avatar
                                                        src={avartarImgUrl}
                                                        fallback={getAvatarFallbackName(profileGas?.fullname)}
                                                        className='h-28 w-28 rounded-full border border-primary text-4xl'
                                                    />
                                                    <div className='absolute bottom-0 right-2'>
                                                        <img src={AvartarChange} alt='camera-icon' />
                                                    </div>
                                                </Label>
                                                <Input
                                                    type='file'
                                                    accept='image/*'
                                                    id='avartar-image'
                                                    onChange={handleImageChange}
                                                    className='hidden'
                                                />
                                            </FormItem>
                                        )}
                                    />
                                    <div className='mt-3 font-semibold text-base'>{myAccount?.fullname}</div>
                                </div>
                            </div>

                            <div className='col-span-1 md:col-span-2'>
                                <EditEmail />
                            </div>

                        </div>
                    </div>

                    {/* Second info */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <FormField
                            control={form.control}
                            name='licenseType'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required={!!watchRoleId && !isAdmin(Number(watchRoleId))}>
                                        {t('field.driver_license_type')}
                                    </FormLabel>
                                    <FormControl>
                                        <Combobox
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.code);
                                                form.setValue('licenseType', item?.code);
                                            }}
                                            items={
                                                licenseTypeGas.map(item => ({
                                                    label: item.name,
                                                    value: item.id?.toString(),
                                                    code: item.id
                                                }))
                                            }
                                            search={false}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='licenseTypeOption'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className='hidden sm:block h-[20px]' />
                                    <FormControl>
                                        <div className='flex items-center min-h-[20px] sm:min-h-[48px]'>
                                            <RadioGroup
                                                key={field.value}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    form.clearErrors('licenseBeforeImageId');
                                                    form.clearErrors('licenseAfterImageId');
                                                }}
                                                value={field.value?.toString()}
                                                className='flex gap-6 w-full'
                                            >
                                                {LICENSE_TYPE_OPTIONS.map((option, index) => (
                                                    <div
                                                        key={index}
                                                        className='flex items-center space-x-2'
                                                    >
                                                        <RadioGroupItem
                                                            value={option.value.toString()}
                                                            id={option.value.toString()}
                                                        />
                                                        <Label className='cursor-pointer font-normal' htmlFor={option.value.toString()}>
                                                            {option.name}
                                                        </Label>
                                                    </div>
                                                ))}
                                            </RadioGroup>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className='col-span-full'>
                            <Label
                                required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                className='mb-2'
                            >
                                {t('field.driver_image')}
                            </Label>
                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                <div>
                                    <ImageUploadAccount
                                        form={form}
                                        filedName='licenseBeforeImageId'
                                        caption={useMyNumberLicense ? t('field.myna_license') : t('words_title.before')}
                                        isUploadOCR={!useMyNumberLicense}
                                        urlImagePreview={beforeImgUrl}
                                        onUploadSuccess={uploadSuccessBefore}
                                        isOcrNoToken={true}
                                        required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                    />
                                </div>
                                {!useMyNumberLicense && (
                                    <div>
                                        <ImageUploadAccount
                                            filedName='licenseAfterImageId'
                                            form={form}
                                            caption={t('words_title.after')}
                                            urlImagePreview={afterImgUrl}
                                            required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <FormField
                            control={form.control}
                            name='fullname'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.name_or_company_name')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='furiganaName'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('field.furigana_name')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='birthOfDate'
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel
                                        required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                    >
                                        {t('field.date_of_birth')}
                                    </FormLabel>
                                    <FormControl>
                                        <Calendar
                                            mode='single'
                                            selected={field.value}
                                            onSelect={date => field.onChange(date)}
                                            required={false}
                                            isInvalid={!!fieldState.error}
                                            useIconRemove={false}
                                            disabled={[
                                                {
                                                    after: new Date()
                                                }
                                            ]}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='phone'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.phone_number')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            maxLength={MAX_LENGTH_NUMBER}
                                            isHalfWidth
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='zipCode'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('field.post_code')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            isHalfWidth
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='address'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('field.address')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='licenseNumber'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('field.driver_license_number')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            isHalfWidth
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='licenseExporeDate'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('field.license_expire_date')}</FormLabel>
                                    <FormControl>
                                        <Calendar
                                            mode='single'
                                            selected={field.value}
                                            onSelect={date => field.onChange(date)}
                                            required={false}
                                            useIconRemove={false}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {isRoleEndUser() && (
                            <div className='col-span-full'>
                                <CouponForeverItem />
                            </div>
                        )}

                    </div>

                </div>

                <div className='mt-8 flex justify-center gap-4'>
                    <ButtonCusWithIcon
                        type='submit'
                        variant='default'
                        size='sm'
                        iconId='#icon-update'
                    >
                        {t('button.update')}
                    </ButtonCusWithIcon>
                </div>

            </form>
        </Form>
    );
});
